/**
 * One decision in the sheet, and the sheet that records a new one.
 *
 * The record reads top to bottom the way a person reviews it: what it settles,
 * why, the asserted box while nobody stands behind it, then provenance,
 * lineage and its neighbours as folds, and the supersede form. The footer is
 * the only place a verb commits, and it reads by standing: Discard / Confirm,
 * Unconfirm / Supersede, or Reactivate. Editing text in place stays
 * impossible: a change of judgment is a supersession.
 *
 * The sheet prints outside the surface's tree, so the API comes in as a prop.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CodeDecision, CodeDecisionRelatedResponse, CodeDecisionScopes } from "@yagni/shared";
import { decisionUrl, workItemUrl } from "@yagni/shared";
import {
  ChevronRightIcon,
  Chip,
  Fold,
  MenuItem,
  MenuSeparator,
  Pill,
  RecordHead,
  SheetFoot,
  Stamp,
} from "../queue/index.js";
import type { DecisionsApi, SupersedeBody } from "./decisionsApi.js";
import {
  STANDING_LABEL,
  citedLabel,
  formatDecisionDate,
  kindLabel,
  relativeDate,
  scopeLabel,
  sourceLabel,
  splitDecisionHeadline,
  standingOf,
} from "./decisionCopy.js";
import { VERB_LABEL, footerVerbs, type DecisionVerb } from "./decisionsModel.js";

export interface DecisionSheetProps {
  api: DecisionsApi;
  decision: CodeDecision | null;
  state: "loading" | "ready" | "missing" | "error";
  error?: string | null;
  onRetry: () => void;
  /** A write landed: re-read the list, the summary and this record. */
  onChanged: () => void;
  hrefFor: (id: string) => string;
  onClose: () => void;
}

export function DecisionSheet({ api, decision, state, error, onRetry, onChanged, hrefFor, onClose }: DecisionSheetProps) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [related, setRelated] = useState<CodeDecisionRelatedResponse | null>(null);
  const [supersede, setSupersede] = useState<"closed" | "write" | "existing">("closed");
  const [copied, setCopied] = useState(false);
  const supersedeRef = useRef<HTMLDetailsElement | null>(null);
  const navigate = useNavigate();

  const id = decision?.id ?? null;
  useEffect(() => {
    setSupersede("closed");
    setActionError(null);
    setRelated(null);
    if (!id) return;
    let cancelled = false;
    api
      .related(id)
      .then((loaded) => {
        if (!cancelled) setRelated(loaded);
      })
      .catch(() => {
        // Neighbours are a reading aid, never the record.
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const run = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      setActionError(null);
      try {
        await action();
        setSupersede("closed");
        onChanged();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "The change did not save");
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const openSupersede = (mode: "write" | "existing") => {
    setSupersede(mode);
    window.requestAnimationFrame(() => supersedeRef.current?.scrollIntoView?.({ block: "nearest" }));
  };

  if (state !== "ready" || !decision) {
    return (
      <>
        <RecordHead recordKey="DECISION" title={state === "loading" ? "Reading the decision" : "Decision"} onClose={onClose} noExpand />
        <div className="sbody" data-testid="decision-sheet-empty">
          {state === "loading" && <p className="body-p">Reading the ledger.</p>}
          {state === "missing" && (
            <p className="body-p" data-testid="decision-detail-missing">
              That decision is not in this workspace's ledger.
            </p>
          )}
          {state === "error" && (
            <div className="stopbox" data-testid="decision-detail-error">
              <div className="h">
                <Stamp>not read</Stamp>
                The ledger did not answer
              </div>
              <p>{error ?? "Failed to load this decision."} The entry may still be here.</p>
              <div>
                <button type="button" className="btn secondary sm" onClick={onRetry}>
                  Try again
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  const standing = standingOf(decision);
  const active = decision.status === "active";
  const contested = (related?.related ?? []).some((r) => r.contested);
  const { headline, rest } = splitDecisionHeadline(decision.decision);
  const kind = kindLabel(decision.kind);
  const verbs = footerVerbs(standing);

  const act = (verb: DecisionVerb) => {
    switch (verb) {
      case "confirm":
        return void run(() => api.verify(decision.id, true));
      case "unconfirm":
        return void run(() => api.verify(decision.id, false));
      case "discard":
        return void run(() => api.discard(decision.id));
      case "reactivate":
        return void run(() => api.reactivate(decision.id));
      case "supersede":
        return openSupersede("write");
    }
  };

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${decisionUrl(decision!.id)}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // The address bar still has the link.
    }
  }

  const pill =
    standing === "assumption" ? (
      <Pill kind="needs">Unconfirmed</Pill>
    ) : standing === "confirmed" ? (
      <Pill kind="live" dot={false}>
        Confirmed
      </Pill>
    ) : (
      <Pill kind="done" dot={false}>
        {STANDING_LABEL[standing]}
      </Pill>
    );

  const more = (
    <>
      {active && <MenuItem onSelect={() => openSupersede("existing")}>Point at an existing decision instead</MenuItem>}
      <MenuItem onSelect={() => void copyLink()}>{copied ? "Copied" : "Copy link"}</MenuItem>
      {decision.workItemId && (
        <>
          <MenuSeparator />
          <MenuItem onSelect={() => navigate(workItemUrl(decision.workItemId!))}>Open the case file</MenuItem>
          {decision.runId && (
            <MenuItem onSelect={() => navigate(`/runs/${encodeURIComponent(decision.workItemId!)}`)}>
              Open the latest run
            </MenuItem>
          )}
        </>
      )}
    </>
  );

  const why: ReactNode =
    standing === "assumption" ? (
      <>
        Confirming tells every agent on <b>{scopeLabel(decision)}</b> to rely on it. Discarding retires it with its record.
      </>
    ) : standing === "confirmed" ? (
      <>Superseding keeps this on the record and points agents at what stands instead.</>
    ) : (
      <>
        Retired. Reactivating puts it back in front of the agents <b>with the standing it had</b>.
      </>
    );

  return (
    <>
      <RecordHead
        recordKey={(kind ?? "Decision").toUpperCase()}
        title={headline}
        onClose={onClose}
        more={more}
        meta={
          <>
            {pill}
            <Chip>{scopeLabel(decision)}</Chip>
            {decision.citedCount > 0 && <Chip>cited ×{decision.citedCount}</Chip>}
            {contested && active && <Chip>contested</Chip>}
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
              recorded {relativeDate(decision.createdAt)}
            </span>
          </>
        }
      />
      <div className="sbody" data-testid="decision-detail">
        {rest && (
          <p className="body-p" data-testid="decision-rest">
            {rest}
          </p>
        )}
        <div className="blk">
          <div className="bh">
            <span className="label">Settles</span>
          </div>
          <p className="brief" data-testid="decision-question">
            {decision.question}
          </p>
        </div>
        {decision.rationale && (
          <div className="blk">
            <div className="bh">
              <span className="label">Why</span>
            </div>
            <p className="body-p" style={{ color: "var(--text-primary)" }}>
              {decision.rationale}
            </p>
          </div>
        )}
        {standing === "assumption" && (
          <div className="stopbox" style={{ background: "var(--accent-subtle)", borderColor: "var(--accent-border)" }} data-testid="asserted-box">
            <div className="h" style={{ color: "var(--accent)" }}>
              <Stamp>asserted</Stamp>
              Not yet confirmed by a person
            </div>
            <p>
              {decision.citedCount > 0
                ? `Agents have already relied on it ${decision.citedCount} time${decision.citedCount === 1 ? "" : "s"}. Confirming makes that official; discarding tells them to stop.`
                : "No agent has cited it yet. Confirm it so they can, or discard it."}
            </p>
          </div>
        )}
        {contested && active && (
          <div className="stopbox" data-testid="contested-box">
            <div className="h">
              <Stamp>contested</Stamp>
              Another live decision answers this differently
            </div>
            <p>Agents refuse to pick between them. Supersede one so they stop asking.</p>
          </div>
        )}
        {actionError && (
          <p className="err-line" role="alert" data-testid="decision-action-error">
            {actionError}
          </p>
        )}

        <div>
          <Fold title="Provenance" defaultOpen>
            <dl className="receipt" data-testid="decision-provenance">
              <dt>Source</dt>
              <dd>{sourceLabel(decision.source)}</dd>
              {kind && (
                <>
                  <dt>Kind</dt>
                  <dd>{kind}</dd>
                </>
              )}
              <dt>Recorded</dt>
              <dd>{formatDecisionDate(decision.createdAt)}</dd>
              {decision.repoFullName ? (
                <>
                  <dt>Repository</dt>
                  <dd>{decision.repoFullName}</dd>
                </>
              ) : (
                <>
                  <dt>Scope</dt>
                  <dd>Workspace-wide</dd>
                </>
              )}
              {decision.teams && decision.teams.length > 0 && (
                <>
                  <dt>{decision.teams.length === 1 ? "Team" : "Teams"}</dt>
                  <dd>
                    {decision.teams.map((t, i) => (
                      <span key={t.id}>
                        {i > 0 ? ", " : ""}
                        <Link to={`/desks/${encodeURIComponent(t.id)}`}>{t.name}</Link>
                      </span>
                    ))}
                  </dd>
                </>
              )}
              {decision.stage && (
                <>
                  <dt>Stage</dt>
                  <dd>{decision.stage}</dd>
                </>
              )}
              {decision.workItemId && (
                <>
                  <dt>Ticket</dt>
                  <dd>
                    <Link to={workItemUrl(decision.workItemId)}>Open the case file</Link>
                  </dd>
                </>
              )}
              {decision.runId && decision.workItemId && (
                <>
                  <dt>Run</dt>
                  <dd>
                    <Link to={`/runs/${encodeURIComponent(decision.workItemId)}`}>Open the latest run</Link>
                  </dd>
                </>
              )}
              <dt>Reliance</dt>
              <dd>
                {decision.citedCount > 0
                  ? `${citedLabel(decision.citedCount)}${decision.lastCitedAt ? `, last on ${formatDecisionDate(decision.lastCitedAt)}` : ""}`
                  : "Not cited by an agent yet"}
              </dd>
              {decision.verification === "confirmed" && decision.verifiedAt && (
                <>
                  <dt>Confirmed</dt>
                  <dd>By a teammate on {formatDecisionDate(decision.verifiedAt)}</dd>
                </>
              )}
            </dl>
          </Fold>
          {related && (related.replacedBy || related.replaces.length > 0) && (
            <Fold title="Lineage" count={(related.replacedBy ? 1 : 0) + related.replaces.length} defaultOpen>
              <div className="rel" data-testid="decision-lineage">
                {related.replacedBy && (
                  <Link to={hrefFor(related.replacedBy.id)}>
                    <span className="f">replaced this{decision.supersededAt ? ` on ${formatDecisionDate(decision.supersededAt)}` : ""}</span>
                    <span className="d">{related.replacedBy.decision}</span>
                    <span className="q">{related.replacedBy.question}</span>
                  </Link>
                )}
                {related.replaces.map((r) => (
                  <Link key={r.id} to={hrefFor(r.id)}>
                    <span className="f">this replaced it</span>
                    <span className="d">{r.decision}</span>
                    <span className="q">{r.question}</span>
                  </Link>
                ))}
              </div>
            </Fold>
          )}
          {related && related.related.length > 0 && (
            <Fold title="Related" count={related.related.length}>
              <div className="rel" data-testid="decision-related">
                {related.related.map((r) => (
                  <Link key={r.decision.id} to={hrefFor(r.decision.id)}>
                    <span className={r.contested ? "f contest" : "f"}>
                      {r.contested ? "contests this" : STANDING_LABEL[standingOf(r.decision)]}
                    </span>
                    <span className="d">{r.decision.decision}</span>
                    <span className="q">{r.decision.question}</span>
                  </Link>
                ))}
              </div>
            </Fold>
          )}
          {active && (
            <SupersedeFold
              key={decision.id}
              foldRef={supersedeRef}
              api={api}
              decision={decision}
              mode={supersede}
              busy={busy}
              onMode={setSupersede}
              onSubmit={(body) => run(() => api.supersede(decision.id, body))}
            />
          )}
        </div>
      </div>
      <SheetFoot
        why={why}
        secondary={verbs.secondary ? { label: VERB_LABEL[verbs.secondary], onClick: () => act(verbs.secondary!), disabled: busy } : undefined}
        primary={{ label: VERB_LABEL[verbs.primary], onClick: () => act(verbs.primary), disabled: busy }}
      />
    </>
  );
}

/**
 * Supersede with something new: write the replacement (the common case), or
 * point at a decision already on the ledger. A blank question takes the
 * superseded decision's own question on the server.
 */
function SupersedeFold({
  foldRef,
  api,
  decision,
  mode,
  busy,
  onMode,
  onSubmit,
}: {
  foldRef: React.RefObject<HTMLDetailsElement | null>;
  api: DecisionsApi;
  decision: CodeDecision;
  mode: "closed" | "write" | "existing";
  busy: boolean;
  onMode: (mode: "closed" | "write" | "existing") => void;
  onSubmit: (body: SupersedeBody) => void;
}) {
  const [replacement, setReplacement] = useState("");
  const [question, setQuestion] = useState("");
  const [rationale, setRationale] = useState("");
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState<CodeDecision[] | null>(null);

  const find = async () => {
    try {
      const result = await api.list({ q: search.trim() || undefined, view: "all" });
      setCandidates(result.items.filter((d) => d.status === "active" && d.id !== decision.id));
    } catch {
      setCandidates([]);
    }
  };

  return (
    <details
      ref={foldRef}
      className="fold"
      open={mode !== "closed"}
      onToggle={(event) => {
        const open = (event.currentTarget as HTMLDetailsElement).open;
        if (open && mode === "closed") onMode("write");
        if (!open && mode !== "closed") onMode("closed");
      }}
      data-testid="supersede-panel"
    >
      <summary>
        <ChevronRightIcon />
        {mode === "existing" ? "Point at an existing decision" : "Supersede with something new"}
        <span className="n" />
      </summary>
      <div className="fb" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {mode === "existing" ? (
          <>
            <form
              className="field"
              onSubmit={(event) => {
                event.preventDefault();
                void find();
              }}
            >
              <label htmlFor="supersede-search">Find</label>
              <div className="inline">
                <div className="in">
                  <input
                    id="supersede-search"
                    aria-label="Search for the successor decision"
                    placeholder="Search decisions"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <button type="submit" className="btn secondary sm">
                  Find
                </button>
              </div>
            </form>
            {candidates && candidates.length === 0 && <p className="body-p">No other active decisions match.</p>}
            {candidates && candidates.length > 0 && (
              <div className="rel">
                {candidates.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    disabled={busy}
                    onClick={() => onSubmit({ successorId: candidate.id })}
                  >
                    <span className="d">{candidate.decision}</span>
                    <span className="q">{candidate.question}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="acts" style={{ border: 0, display: "flex", gap: 6 }}>
              <button type="button" className="btn quiet sm" onClick={() => onMode("write")}>
                Write what stands instead
              </button>
            </div>
          </>
        ) : (
          <form
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (!replacement.trim()) return;
              onSubmit({
                decision: replacement.trim(),
                question: question.trim() || undefined,
                rationale: rationale.trim() || undefined,
              });
            }}
          >
            <div className="field top">
              <label htmlFor="supersede-decision">Decision</label>
              <textarea
                id="supersede-decision"
                aria-label="Replacement decision"
                rows={2}
                placeholder="What stands instead"
                value={replacement}
                onChange={(event) => setReplacement(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="supersede-question">Settles</label>
              <div className="in">
                <input
                  id="supersede-question"
                  aria-label="Replacement question"
                  placeholder={decision.question}
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                />
              </div>
            </div>
            <div className="field top">
              <label htmlFor="supersede-why">Why</label>
              <textarea
                id="supersede-why"
                aria-label="Replacement rationale"
                rows={2}
                placeholder="Why (optional)"
                value={rationale}
                onChange={(event) => setRationale(event.target.value)}
              />
            </div>
            <div className="acts" style={{ border: 0, display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button type="submit" className="btn secondary sm" disabled={busy || !replacement.trim()}>
                Supersede with this
              </button>
            </div>
          </form>
        )}
      </div>
    </details>
  );
}

export interface NewDecisionSheetProps {
  api: DecisionsApi;
  scopes: CodeDecisionScopes | null;
  defaultRepo?: string;
  onRecorded: (id: string) => void;
  onOpenExisting: (id: string) => void;
  onCancel: () => void;
}

/**
 * Record a decision by hand: the decision, what it settles, why, and a scope.
 * Confirmed by construction on the server. A near-duplicate comes back as the
 * existing record, and the sheet offers it instead of banking a rival.
 */
export function NewDecisionSheet({ api, scopes, defaultRepo, onRecorded, onOpenExisting, onCancel }: NewDecisionSheetProps) {
  const [decision, setDecision] = useState("");
  const [question, setQuestion] = useState("");
  const [rationale, setRationale] = useState("");
  const [repo, setRepo] = useState(defaultRepo ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<CodeDecision | null>(null);

  const canSubmit = question.trim().length > 0 && decision.trim().length > 0 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setDuplicate(null);
    try {
      const result = await api.record({
        question: question.trim(),
        decision: decision.trim(),
        rationale: rationale.trim() || undefined,
        repoFullName: repo || undefined,
      });
      if (result.kind === "duplicate") {
        setDuplicate(result.existing);
        return;
      }
      onRecorded(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The decision did not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <RecordHead
        recordKey="RECORD"
        title="Record a decision"
        onClose={onCancel}
        noExpand
        meta={
          <>
            <Chip>by hand</Chip>
            <span className="fact" style={{ fontSize: 11.5 }}>
              confirmed on record, since you wrote it
            </span>
          </>
        }
      />
      <form
        className="sbody"
        data-testid="record-decision-sheet"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="field top">
          <label htmlFor="record-decision">Decision</label>
          <textarea
            id="record-decision"
            aria-label="Decision"
            rows={2}
            placeholder="One sentence that settles it"
            value={decision}
            onChange={(event) => setDecision(event.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="record-question">Settles</label>
          <div className="in">
            <input
              id="record-question"
              aria-label="Question"
              placeholder="The question it answers"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </div>
        </div>
        <div className="field top">
          <label htmlFor="record-why">Why</label>
          <textarea
            id="record-why"
            aria-label="Why"
            rows={3}
            placeholder="What made this the call"
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="record-scope">Scope</label>
          <select id="record-scope" aria-label="Scope" value={repo} onChange={(event) => setRepo(event.target.value)}>
            <option value="">Workspace-wide</option>
            {(scopes?.repos ?? []).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        {duplicate ? (
          <div className="dashed" style={{ padding: "10px 12px", fontSize: 12.5, display: "flex", flexDirection: "column", gap: 8 }} data-testid="record-duplicate">
            <span style={{ color: "var(--text-primary)" }}>A decision on that question is already recorded.</span>
            <span>
              {duplicate.decision} · {duplicate.question}
            </span>
            <span>
              <button type="button" className="btn secondary sm" onClick={() => onOpenExisting(duplicate.id)}>
                Open it
              </button>
            </span>
          </div>
        ) : (
          <div className="dashed" style={{ padding: "10px 12px", fontSize: 12.5 }}>
            A decision on a similar question may already exist. YAGNI checks when you record and offers to open it.
          </div>
        )}
        {error && (
          <p className="err-line" role="alert" data-testid="record-error">
            {error}
          </p>
        )}
      </form>
      <SheetFoot
        why={<>Recorded decisions are read by every agent on that scope before it plans or builds.</>}
        secondary={{ label: "Cancel", onClick: onCancel }}
        primary={{ label: "Record decision", onClick: () => void submit(), disabled: !canSubmit }}
      />
    </>
  );
}
